import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createUser,
  listUsers,
  type AuthMethod,
  type Folder,
  type NewUser,
  type User,
} from '../api';
import { FolderEditor } from '../components/Folders';
import { OneTimeSecret } from '../components/OneTimeSecret';
import { useCan } from '../staff';

export function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>();
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const mayCreate = useCan('roster:write');

  const reload = async () => {
    setError(undefined);
    try {
      setUsers(await listUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  return (
    <section>
      <header className="page-header">
        <h1>SFTP users</h1>
        {mayCreate && (
          <button type="button" onClick={() => setCreating((open) => !open)}>
            {creating ? 'Cancel' : 'New user'}
          </button>
        )}
      </header>

      {creating && mayCreate && (
        <CreateUser
          onCreated={() => {
            setCreating(false);
            void reload();
          }}
        />
      )}

      {error && <p className="error">{error}</p>}
      {!users ? (
        <p>Loading…</p>
      ) : users.length === 0 ? (
        <p className="hint">No users yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Sign-in</th>
              <th>Folders</th>
              <th>Account</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.username}>
                <td>
                  <Link to={`/users/${encodeURIComponent(user.username)}`}>{user.username}</Link>
                </td>
                <td>{AUTH_LABELS[user.authMethod]}</td>
                <td>
                  {user.folders.map((folder) => folder.path).join(', ')}
                  {/* Flagged on the roster because this is the fact that used to be
                      invisible until somebody opened the profile and knew what
                      sharedPath meant. */}
                  <Shares user={user} />
                </td>
                <td>
                  <AccountBadge user={user} />
                </td>
                <td className="hint">{user.comment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Counted from sharedPath, which is on the roster response already -- no second
// request. It links to the access map rather than trying to summarise here, since
// "reaches another company" is only useful with the answer to "whose, and how
// much".
function Shares({ user }: { user: User }) {
  const shared = user.folders.filter((folder) => folder.sharedPath);
  if (shared.length === 0) return null;
  return (
    <p className="hint">
      <span className="badge warn">
        {shared.length === 1 ? 'reaches another tree' : `reaches ${shared.length} other trees`}
      </span>{' '}
      <Link to={`/access?who=${encodeURIComponent(user.username)}`}>what exactly?</Link>
    </p>
  );
}

const AUTH_LABELS: Record<AuthMethod, string> = {
  'ssh-key': 'SSH key',
  password: 'Password',
  'ssh-key-or-password': 'Key or password',
};

// The states worth distinguishing to someone deciding whether a partner can
// currently log in. MISSING is the one that needs action: a profile whose Cognito
// account is gone, which is the visible half of a create that failed part-way.
function AccountBadge({ user }: { user: User }) {
  if (!user.account.exists) {
    return <span className="badge bad">no account — recreate the user</span>;
  }
  if (!user.account.enabled) return <span className="badge bad">disabled</span>;
  if (user.account.status === 'FORCE_CHANGE_PASSWORD' && user.authMethod === 'ssh-key') {
    // Normal, not a problem: a key-only account never gets a usable password, so
    // Cognito leaves it in this state forever and the auth Lambda ignores it.
    return <span className="badge ok">key auth</span>;
  }
  if (user.account.status === 'FORCE_CHANGE_PASSWORD') {
    return <span className="badge warn">password not set</span>;
  }
  return <span className="badge ok">{user.account.status.toLowerCase()}</span>;
}

const EMPTY: NewUser = {
  username: '',
  email: '',
  authMethod: 'ssh-key',
  folders: [{ path: '', permission: 'read-write' }],
};

function CreateUser({ onCreated }: { onCreated: () => void }) {
  const [draft, setDraft] = useState<NewUser>(EMPTY);
  const [keysText, setKeysText] = useState('');
  const [ipsText, setIpsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [created, setCreated] = useState<{ username: string; password?: string }>();

  const needsKey = draft.authMethod !== 'password';
  const needsPassword = draft.authMethod !== 'ssh-key';

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const body: NewUser = {
        ...draft,
        username: draft.username.trim(),
        email: draft.email.trim(),
        folders: draft.folders.map((folder) => ({
          ...folder,
          path: folder.path.trim(),
          comment: folder.comment?.trim() || undefined,
        })),
        // Sent only when the auth method uses them; the API rejects keys on a
        // password-only user rather than storing keys that would be ignored.
        publicKeys: needsKey ? splitLines(keysText) : undefined,
        // Absent means "any source IP". An empty list is rejected by the API on
        // purpose, because it reads like "deny everything" but is ignored.
        allowedSourceIps: splitList(ipsText).length > 0 ? splitList(ipsText) : undefined,
        comment: draft.comment?.trim() || undefined,
        generatePassword: needsPassword ? true : undefined,
      };
      const result = await createUser(body);
      setCreated({ username: result.profile.username, password: result.password });
      setDraft(EMPTY);
      setKeysText('');
      setIpsText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="card wide">
        <h2>{created.username} created</h2>
        {created.password ? (
          <OneTimeSecret
            label="Password"
            value={created.password}
            note={`Hand this to ${created.username} over a channel you trust. It is not stored anywhere and cannot be shown again — the only way to recover from losing it is to reset it.`}
          />
        ) : (
          <p>This user signs in with their SSH key, so there is no password to hand over.</p>
        )}
        <div className="actions">
          <Link className="button" to={`/users/${encodeURIComponent(created.username)}`}>
            Open {created.username}
          </Link>
          <button type="button" className="link" onClick={onCreated}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="card wide"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h2>New SFTP user</h2>

      <div className="grid">
        <label>
          Username
          <input
            value={draft.username}
            onChange={(event) => setDraft({ ...draft, username: event.target.value })}
            placeholder="acme-labs"
            spellCheck={false}
            required
          />
          <span className="hint">
            Also their SFTP login and the name of their home directory. It cannot be changed later.
          </span>
        </label>
        <label>
          Email
          <input
            type="email"
            value={draft.email}
            onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            required
          />
          <span className="hint">Where password resets go. Marked verified on their behalf.</span>
        </label>
      </div>

      <label>
        How they sign in
        <select
          value={draft.authMethod}
          onChange={(event) => setDraft({ ...draft, authMethod: event.target.value as AuthMethod })}
        >
          <option value="ssh-key">SSH key only</option>
          <option value="password">Password only</option>
          <option value="ssh-key-or-password">SSH key or password</option>
        </select>
      </label>

      {needsKey && (
        <label>
          Public keys
          <textarea
            rows={4}
            value={keysText}
            onChange={(event) => setKeysText(event.target.value)}
            placeholder="ssh-ed25519 AAAAC3Nza… partner@host"
            spellCheck={false}
            required
          />
          <span className="hint">
            One per line, from their <code>.pub</code> file. If they cannot produce one, create the
            user and generate a keypair on their page. Never paste a private key.
          </span>
        </label>
      )}

      {needsPassword && (
        <p className="hint">
          A password will be generated and shown once, on the next screen. Passwords are never
          stored in this repo or anywhere outside Cognito.
        </p>
      )}

      <fieldset>
        <legend>Folders</legend>
        <FolderEditor
          folders={draft.folders}
          onChange={(folders: Folder[]) => setDraft({ ...draft, folders })}
        />
      </fieldset>

      <div className="grid">
        <label>
          Allowed source IPs (optional)
          <input
            value={ipsText}
            onChange={(event) => setIpsText(event.target.value)}
            placeholder="203.0.113.0/24, 198.51.100.7"
            spellCheck={false}
          />
          <span className="hint">Leave empty to allow any address.</span>
        </label>
        <label>
          Note (optional)
          <input
            value={draft.comment ?? ''}
            onChange={(event) => setDraft({ ...draft, comment: event.target.value })}
            placeholder="who this is and why they have access"
          />
        </label>
      </div>

      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy}>
        {busy ? 'Creating…' : 'Create user'}
      </button>
    </form>
  );
}

const splitLines = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

const splitList = (value: string) =>
  value
    .split(/[,\s]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
